import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { createNewsUrl } from '../../../utils/urlUtils';
import { mockDiscussions } from '../../../mocks/discussions';

const newsItems = [
  {
    id: 1,
    title: 'New UAE Production Studio Opens in Dubai Media City',
    excerpt: 'A state-of-the-art production facility has opened in Dubai, promising to revolutionize Arab drama production.',
    category: 'Production',
    timestamp: '2 hours ago',
    isBreaking: true,
    image: 'https://public.readdy.ai/ai/img_res/7c88cae87b3da026b0f0d7c69e28cc9d.jpg'
  },
  {
    id: 2,
    title: 'Streaming Platform Announces $50M Investment in Arab Content',
    excerpt: 'A leading streaming service commits to producing original Arab short dramas, signaling growing international interest.',
    category: 'Streaming',
    timestamp: '4 hours ago',
    isBreaking: false,
    image: 'https://readdy.ai/api/search-image?query=Digital%20streaming%20platform%20interface%20on%20large%20screen%20display%20showing%20Arabic%20drama%20content%2C%20modern%20tech%20environment%2C%20warm%20golden%20lighting&width=400&height=250&seq=news2&orientation=landscape'
  },
  {
    id: 3,
    title: 'Arab Drama Awards 2024: Nominations Announced',
    excerpt: 'Desert Winds and The Last Garden lead with multiple nominations across various categories.',
    category: 'Awards',
    timestamp: '6 hours ago',
    isBreaking: false,
    image: 'https://readdy.ai/api/search-image?query=Elegant%20award%20ceremony%20stage%20with%20golden%20trophy%20and%20dramatic%20lighting%2C%20luxurious%20Middle%20Eastern%20event%20venue%2C%20amber%20and%20gold%20color%20scheme&width=400&height=250&seq=news3&orientation=landscape'
  },
  {
    id: 4,
    title: 'Rising Star Layla Al-Rashid Signs Exclusive Deal',
    excerpt: 'Following her breakout performance in Noor, the actress signs a three-year deal with a major network.',
    category: 'Casting',
    timestamp: '12 hours ago',
    isBreaking: false,
    image: 'https://readdy.ai/api/search-image?query=Professional%20film%20contract%20signing%20ceremony%20with%20elegant%20pen%20and%20documents%20on%20wooden%20desk%2C%20warm%20ambient%20lighting%2C%20sophisticated%20business%20setting&width=400&height=250&seq=news4&orientation=landscape'
  }
];

// Get Industry News posts from Forum
const forumNewsPosts = mockDiscussions
  .filter(d => d.category === 'news')
  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

const getCategoryColor = (category: string) => {
  const colors: Record<string, string> = {
    'Production': 'bg-amber-100 text-amber-700',
    'Streaming': 'bg-orange-100 text-orange-700',
    'Awards': 'bg-yellow-100 text-yellow-700',
    'Casting': 'bg-rose-100 text-rose-700',
    'Technology': 'bg-teal-100 text-teal-700'
  };
  return colors[category] || 'bg-gray-100 text-gray-700';
};

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

const LatestNews = () => {
  const featuredNews = newsItems[0];
  const otherNews = newsItems.slice(1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || forumNewsPosts.length === 0) return;

    const scrollSpeed = 1;
    const intervalTime = 30;

    const scroll = () => {
      if (isPaused) return;
      
      // Calculate the width of one set of posts (half of total since we duplicate)
      const singleSetWidth = container.scrollWidth / 2;
      
      // When we've scrolled past the first set, instantly reset to the beginning
      // This creates a seamless loop since the second set is identical to the first
      if (container.scrollLeft >= singleSetWidth) {
        container.scrollLeft = 0;
      } else {
        container.scrollLeft += scrollSpeed;
      }
    };

    const intervalId = setInterval(scroll, intervalTime);

    return () => clearInterval(intervalId);
  }, [isPaused]);

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-2xl p-6 border border-amber-100">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
            <i className="ri-newspaper-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Latest News</h2>
            <p className="text-sm text-gray-500">Breaking stories from the drama world</p>
          </div>
        </div>
        <Link 
          to="/news" 
          className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center whitespace-nowrap"
        >
          See All
          <i className="ri-arrow-right-line ml-1"></i>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Featured News */}
        <Link to={createNewsUrl(featuredNews.id, featuredNews.title)} className="group cursor-pointer">
          <div className="relative rounded-xl overflow-hidden">
            <img 
              src={featuredNews.image} 
              alt={featuredNews.title}
              className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            
            {featuredNews.isBreaking && (
              <div className="absolute top-4 left-4 flex items-center gap-1 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                <i className="ri-alarm-warning-line"></i>
                Breaking
              </div>
            )}
            
            <div className="absolute bottom-4 left-4 right-4">
              <span className={`inline-block px-2 py-1 text-xs rounded-full mb-2 ${getCategoryColor(featuredNews.category)}`}>
                {featuredNews.category}
              </span>
              <h3 className="text-white font-bold text-lg mb-2 group-hover:text-amber-300 transition-colors">
                {featuredNews.title}
              </h3>
              <p className="text-gray-300 text-sm line-clamp-2">{featuredNews.excerpt}</p>
              <span className="text-gray-400 text-xs mt-2 block">{featuredNews.timestamp}</span>
            </div>
          </div>
        </Link>

        {/* Other News */}
        <div className="space-y-4">
          {otherNews.map((news) => (
            <Link 
              key={news.id} 
              to={createNewsUrl(news.id, news.title)}
              className="flex gap-4 bg-white rounded-xl p-3 hover:shadow-md transition-shadow cursor-pointer group"
            >
              <img 
                src={news.image} 
                alt={news.title}
                className="w-24 h-20 object-cover object-top rounded-lg flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getCategoryColor(news.category)}`}>
                    {news.category}
                  </span>
                  <span className="text-gray-400 text-xs">{news.timestamp}</span>
                </div>
                <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-amber-600 transition-colors">
                  {news.title}
                </h4>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Forum Industry News - Auto-scrolling Carousel */}
      <div className="mt-6">
        <div className="relative overflow-hidden">
          {/* Auto-scrolling container */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-hidden"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Duplicate posts for seamless loop */}
            {[...forumNewsPosts, ...forumNewsPosts].map((post, index) => (
              <Link
                key={`${post.id}-${index}`}
                to={`/forum/news/${post.id}-${post.slug}`}
                className="flex-shrink-0 w-80 bg-white rounded-lg border border-gray-200 hover:border-amber-500 transition-all hover:shadow-md cursor-pointer p-4"
              >
                {/* Post Header */}
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={post.author.avatar}
                    alt={post.author.username}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm truncate">
                        {post.author.username}
                      </span>
                      {(post as any).isBreaking && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full whitespace-nowrap">
                          Hot
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{formatTimeAgo(post.createdAt)}</span>
                  </div>
                </div>

                {/* Post Content */}
                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-sm">
                  {post.title}
                </h4>
                <p className="text-gray-600 text-xs line-clamp-2 mb-3">
                  {post.content}
                </p>

                {/* Post Stats */}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-arrow-up-line"></i>
                    {post.upvotes}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {post.commentCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {post.views}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LatestNews;
